import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getData, serverURL } from "../../../../Services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// API CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://your-api.com/api";

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

// ─────────────────────────────────────────────────────────────────────────────
// API FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────
const apiDeleteDrivers = async (ids) => {
  try {
    const res = await fetch(`${API_BASE}/drivers/bulk-delete`, {
      method: "DELETE",
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  } catch (error) {
    console.error("Delete drivers error:", error);
    throw error;
  }
};

const apiAddDriver = async (payload) => {
  try {
    const res = await fetch(`${API_BASE}/drivers`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  } catch (error) {
    console.error("Add driver error:", error);
    throw error;
  }
};

const apiVerifyDocuments = async (driverId, documents) => {
  try {
    const res = await fetch(`${API_BASE}/drivers/${driverId}/verify-documents`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ documents }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  } catch (error) {
    console.error("Verify documents error:", error);
    throw error;
  }
};

const apiAssignOrder = async (driverId, orderId, isManual = true) => {
  try {
    const res = await fetch(`${API_BASE}/orders/assign`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ driverId, orderId, type: isManual ? "manual" : "auto" }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  } catch (error) {
    console.error("Assign order error:", error);
    throw error;
  }
};

const apiGetDriverEarnings = async (driverId) => {
  try {
    const res = await fetch(`${API_BASE}/drivers/${driverId}/earnings`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  } catch (error) {
    console.error("Get earnings error:", error);
    throw error;
  }
};

const apiGetDriverWallet = async (driverId) => {
  try {
    const res = await fetch(`${API_BASE}/drivers/${driverId}/wallet`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  } catch (error) {
    console.error("Get wallet error:", error);
    throw error;
  }
};

const apiGetDriverLiveLocation = async (driverId) => {
  try {
    const res = await fetch(`${API_BASE}/drivers/${driverId}/live-location`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  } catch (error) {
    console.error("Get live location error:", error);
    throw error;
  }
};

const apiGetDriverDeliveryHistory = async (driverId) => {
  try {
    const res = await fetch(`${API_BASE}/drivers/${driverId}/delivery-history`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  } catch (error) {
    console.error("Get delivery history error:", error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// ICONS (optimized sizes)
// ─────────────────────────────────────────────────────────────────────────────
const EditIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const TrashIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);
const SearchIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
const ChevronLeftIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>);
const ChevronRightIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>);
const PlusIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const LocationIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>);
const WalletIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 12v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5z"/><path d="M22 12h-6a2 2 0 0 0-2 2 2 2 0 0 0 2 2h6"/><line x1="18" y1="9" x2="18" y2="9.01"/></svg>);
const DocumentIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>);
const HistoryIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const OrderIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>);
const EarningsIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>);
const ViewMoreIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>);

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE COMPONENT - FULLY FIXED
// ─────────────────────────────────────────────────────────────────────────────
const DriverImage = ({ picName, name, theme }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Debug logging
  console.log("DriverImage - picName received:", picName);
  console.log("DriverImage - serverURL:", serverURL);
  
  // Get clean filename and construct URL
  const getImageUrl = () => {
    if (!picName || picName.trim() === "" || picName === "NULL" || picName === "null") {
      console.log("No valid picName provided");
      return null;
    }
    
    // Extract just the filename (remove any path)
    let filename = picName;
    if (filename.includes('/')) {
      filename = filename.split('/').pop();
    }
    if (filename.includes('\\')) {
      filename = filename.split('\\').pop();
    }
    
    // Construct the correct URL
    const url = `${serverURL}/public/uploadnew/upload/${filename}`;
    console.log("Constructed image URL:", url);
    return url;
  };
  
  const imageUrl = getImageUrl();
  
  const handleImageLoad = () => {
    console.log("Image loaded successfully:", imageUrl);
    setIsLoading(false);
  };
  
  const handleImageError = () => {
    console.error("Image failed to load:", imageUrl);
    setImageError(true);
    setIsLoading(false);
  };
  
  // If no image URL or image failed to load, show placeholder with initials
  if (!imageUrl || imageError) {
    const initial = name ? name.charAt(0).toUpperCase() : "D";
    return (
      <div style={{ 
        width: "28px", 
        height: "28px", 
        borderRadius: "50%", 
        background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        fontSize: "12px", 
        fontWeight: 600, 
        color: "#fff" 
      }}>
        {initial}
      </div>
    );
  }
  
  // If still loading, show loading spinner (but with timeout to prevent infinite loading)
  if (isLoading) {
    // Set a timeout to prevent infinite loading
    setTimeout(() => {
      if (isLoading) {
        console.log("Loading timeout - forcing error state");
        setImageError(true);
        setIsLoading(false);
      }
    }, 5000);
    
    return (
      <div style={{ 
        width: "28px", 
        height: "28px", 
        borderRadius: "50%", 
        background: theme === 'dark' ? "#1a2035" : "#f3f4f6",
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center"
      }}>
        <div style={{
          width: "12px",
          height: "12px",
          border: `2px solid ${theme === 'dark' ? "#3a4a6b" : "#d1d5db"}`,
          borderTop: "2px solid #3b82f6",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite"
        }} />
      </div>
    );
  }
  
  // Show actual image
  return (
    <img 
      src={imageUrl}
      alt={name}
      onLoad={handleImageLoad}
      onError={handleImageError}
      style={{
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        objectFit: "cover"
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (optimized sizes)
// ─────────────────────────────────────────────────────────────────────────────

const StatusBadge = ({ status, theme }) => {
  const isActive = status === "Active";
  const displayStatus = isActive ? "Active" : "Inactive";
  const config = { 
    Active: { bg: "rgba(20,184,166,0.15)", color: "#14b8a6", dot: "#14b8a6" }, 
    Inactive: { bg: "rgba(148,163,184,0.12)", color: "#94a3b8", dot: "#94a3b8" } 
  };
  const c = config[displayStatus] || config.Inactive;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px 2px 6px", borderRadius: "16px", background: c.bg, border: `1px solid ${c.bg}` }}>
      <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: c.dot }} />
      <span style={{ fontSize: "9px", fontWeight: 600, color: c.color }}>{displayStatus}</span>
    </div>
  );
};

const ReadyBadge = ({ is_ready, theme }) => {
  const ready = is_ready === "1" ? "Yes" : "No";
  const config = { 
    Yes: { bg: "rgba(20,184,166,0.15)", color: "#14b8a6" }, 
    No: { bg: "rgba(245,158,11,0.12)", color: "#f59e0b" } 
  };
  const c = config[ready] || config.No;
  return <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "9px", fontWeight: 600, background: c.bg, color: c.color }}>{ready}</span>;
};

const OnOffBadge = ({ open_status, theme }) => {
  const value = open_status === "1" ? "ON" : "OFF";
  const config = { 
    ON: { bg: "rgba(34,197,94,0.15)", color: "#22c55e" }, 
    OFF: { bg: "rgba(148,163,184,0.12)", color: "#94a3b8" } 
  };
  const c = config[value] || config.OFF;
  return <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "9px", fontWeight: 700, background: c.bg, color: c.color }}>{value}</span>;
};

const VerificationBadge = ({ driver, theme }) => {
  const isVerified = driver.is_verify_personalinfo === "1" && driver.is_verify_docinfo === "1" && driver.is_verify_vehicle === "1" && driver.is_verify_bankinfo === "1" && driver.is_verify_emergency === "1";
  return <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "9px", fontWeight: 600, background: isVerified ? "rgba(20,184,166,0.15)" : "rgba(245,158,11,0.12)", color: isVerified ? "#14b8a6" : "#f59e0b" }}>{isVerified ? "Verified" : "Pending"}</span>;
};

const Checkbox = ({ checked, onChange, theme }) => {
  const borderColor = checked ? "#3b82f6" : (theme === 'dark' ? "#3a4460" : "#d1d5db");
  return (
    <div onClick={onChange} style={{ 
      width: "14px", height: "14px", borderRadius: "3px", 
      border: checked ? "2px solid #3b82f6" : `2px solid ${borderColor}`, 
      background: checked ? "#3b82f6" : "transparent", 
      display: "flex", alignItems: "center", justifyContent: "center", 
      cursor: "pointer", transition: "all 0.15s ease" 
    }}>
      {checked && (
        <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
          <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      )}
    </div>
  );
};

const Toast = ({ message, type, onClose, theme }) => {
  return (
    <div style={{ 
      position: "fixed", bottom: "24px", right: "24px", zIndex: 999, 
      background: type === "error" ? (theme === 'dark' ? "#1a0a0a" : "#fef2f2") : (theme === 'dark' ? "#0a1a0a" : "#f0fdf4"), 
      border: `1px solid ${type === "error" ? "rgba(239,68,68,0.4)" : "rgba(16,185,129,0.4)"}`, 
      borderRadius: "10px", padding: "10px 16px", 
      display: "flex", alignItems: "center", gap: "8px", 
      boxShadow: theme === 'dark' ? "0 8px 32px rgba(0,0,0,0.5)" : "0 8px 32px rgba(0,0,0,0.1)", 
      animation: "slideUp 0.25s ease" 
    }}>
      <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: type === "error" ? "#ef4444" : "#10b981" }} />
      <span style={{ fontSize: "12px", color: type === "error" ? (theme === 'dark' ? "#fca5a5" : "#dc2626") : (theme === 'dark' ? "#6ee7b7" : "#059669"), flex: 1 }}>{message}</span>
      <span onClick={onClose} style={{ cursor: "pointer", color: "#64748b", fontSize: "14px" }}>×</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT - DriverTable
// ─────────────────────────────────────────────────────────────────────────────
const DriverTable = ({ isDark = true }) => {
  const navigate = useNavigate();
  const theme = isDark ? 'dark' : 'light';
  
  const colors = {
    background: isDark ? "#0d1117" : "#f9fafb",
    surface: isDark ? "#141824" : "#ffffff",
    surfaceHover: isDark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.02)",
    surfaceLighter: isDark ? "#1a2035" : "#f3f4f6",
    border: isDark ? "#1e2740" : "#e5e7eb",
    borderLight: isDark ? "#1a2035" : "#f3f4f6",
    textPrimary: isDark ? "#f1f5f9" : "#111827",
    textSecondary: isDark ? "#94a3b8" : "#6b7280",
    textMuted: isDark ? "#64748b" : "#9ca3af",
    inputBg: isDark ? "#0f1520" : "#f9fafb",
    inputBorder: isDark ? "#1e2740" : "#e5e7eb",
    selectedBg: isDark ? "rgba(59,130,246,0.07)" : "rgba(79,70,229,0.05)",
    buttonPrimaryBg: isDark ? "#1e3a8a" : "#eef2ff",
    buttonPrimaryColor: isDark ? "#93c5fd" : "#4f46e5",
    modalBg: isDark ? "#141824" : "#ffffff",
    modalBorder: isDark ? "#1e2740" : "#e5e7eb",
    deleteBg: isDark ? "rgba(239,68,68,0.15)" : "#fef2f2",
    deleteColor: "#ef4444",
    addBtnBg: "#4a6cf7",
    exportBtnBg: "#28a745",
    filterBtnActiveBg: "#3b82f6",
    filterBtnActiveColor: "#fff",
    filterBtnInactiveBg: "transparent",
    filterBtnInactiveColor: isDark ? "#64748b" : "#6b7280",
    filterBtnInactiveBorder: isDark ? "#1e2740" : "#e5e7eb",
    tableHeaderBg: isDark ? "#0f1520" : "#f9fafb",
    tableRowBorder: isDark ? "#1a2035" : "#f3f4f6",
  };
  
  // State
  const [drivers, setDrivers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [readyFilter, setReadyFilter] = useState("All");
  const [onOffFilter, setOnOffFilter] = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDriver, setNewDriver] = useState({});
  
  // State for modals
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAssignOrderModal, setShowAssignOrderModal] = useState(false);
  const [assignOrderData, setAssignOrderData] = useState({ orderId: "", type: "manual" });
  const [driverEarnings, setDriverEarnings] = useState(null);
  const [driverWallet, setDriverWallet] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [driverHistory, setDriverHistory] = useState([]);

  const showToast = (message, type = "success") => { 
    setToast({ message, type }); 
    setTimeout(() => setToast(null), 3500); 
  };

  useEffect(() => { 
    const t = setTimeout(() => { 
      setDebouncedSearch(search); 
      setPage(1); 
    }, 500); 
    return () => clearTimeout(t); 
  }, [search]);

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData("drivers/Riders");
      console.log("API Response:", res);
      
      let driversData = [];
      if (res && res.status === true) {
        driversData = res.data || [];
      } else if (Array.isArray(res)) {
        driversData = res;
      } else if (res && res.drivers && Array.isArray(res.drivers)) {
        driversData = res.drivers;
      } else if (res && res.data && Array.isArray(res.data)) {
        driversData = res.data;
      } else {
        driversData = [];
      }
      
      // Log first driver to debug pic field
      if (driversData.length > 0) {
        console.log("First driver data:", driversData[0]);
        console.log("Driver pic value:", driversData[0].pic);
      }
      
      setDrivers(driversData);
      setTotal(driversData.length);
    } catch (error) {
      console.error("Fetch drivers error:", error);
      showToast(`Failed to load drivers: ${error.message}`, "error");
      setDrivers([]);
      setTotal(0);
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => { 
    fetchDrivers(); 
  }, [fetchDrivers]);

  // Apply filters
  let filteredDrivers = [...drivers];
  
  if (debouncedSearch) {
    filteredDrivers = filteredDrivers.filter(d => 
      (d.name || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (d.contact || '').includes(debouncedSearch) ||
      (d.email || '').toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }
  
  if (statusFilter !== "All") {
    filteredDrivers = filteredDrivers.filter(d => 
      statusFilter === "Active" ? d.status === "1" : d.status !== "1"
    );
  }
  
  if (readyFilter !== "All") {
    filteredDrivers = filteredDrivers.filter(d => 
      readyFilter === "Yes" ? d.is_ready === "1" : d.is_ready !== "1"
    );
  }
  
  if (onOffFilter !== "All") {
    filteredDrivers = filteredDrivers.filter(d => 
      onOffFilter === "ON" ? d.open_status === "1" : d.open_status !== "1"
    );
  }
  
  const startIndex = (page - 1) * perPage;
  const paginatedDrivers = filteredDrivers.slice(startIndex, startIndex + perPage);
  const totalFiltered = filteredDrivers.length;
  const totalPages = Math.ceil(totalFiltered / perPage);

  const mappedDrivers = paginatedDrivers.map((d, idx) => ({ 
    ...d, 
    sn: startIndex + idx + 1, 
    displayName: d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || `Driver ${d.id}`,
    displayWallet: `₹${parseFloat(d.wallet || 0).toLocaleString()}`,
    displayDate: d.created_at ? new Date(d.created_at).toLocaleDateString('en-IN') : d.rd || 'N/A',
    statusText: d.status === "1" ? "Active" : "Inactive",
    is_ready: d.is_ready || "0",
    open_status: d.open_status || "0",
    contact: d.contact || d.primary_number || d.whatsapp_number || 'N/A',
  }));

  const pageIds = mappedDrivers.map(d => d.id?.toString());
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  
  const toggleAll = () => { 
    setSelected(prev => { 
      const next = new Set(prev); 
      if (allPageSelected) {
        pageIds.forEach(id => next.delete(id));
      } else {
        pageIds.forEach(id => next.add(id));
      }
      return next; 
    }); 
  };
  
  const toggleOne = (id) => { 
    setSelected(prev => { 
      const n = new Set(prev); 
      if (n.has(id)) {
        n.delete(id);
      } else {
        n.add(id);
      }
      return n; 
    }); 
  };
  
  const clearSelection = () => setSelected(new Set());

  const handleAddDriverNavigate = () => {
    navigate("/Dashboard/AddNewDriver", { state: { mode: "add" } });
  };

  const handleEditDriverNavigate = (driver) => {
    navigate("/Dashboard/AddNewDriver", { state: { mode: "edit", driverData: driver } });
  };

  const handleViewMoreClick = (driver) => {
    navigate("/Dashboard/DriverDetails", { state: { driver } });
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    setActionLoading(true);
    try { 
      await apiDeleteDrivers(Array.from(selected)); 
      showToast(`${selected.size} driver(s) deleted`); 
      clearSelection(); 
      setConfirmDelete(false); 
      fetchDrivers(); 
    } catch (err) { 
      showToast(`Delete failed: ${err.message}`, "error"); 
    } finally { 
      setActionLoading(false); 
    }
  };

  const verifyDocument = async (docType) => {
    if (!selectedDriver) return;
    setActionLoading(true);
    try { 
      await apiVerifyDocuments(selectedDriver.id, { [docType]: "Verified" }); 
      showToast(`${docType} verified successfully`); 
      setShowDocumentsModal(false); 
      fetchDrivers(); 
    } catch (err) { 
      showToast(`Verification failed: ${err.message}`, "error"); 
    } finally { 
      setActionLoading(false); 
    }
  };

  const handleViewEarnings = async (driver) => {
    setSelectedDriver(driver);
    setShowEarningsModal(true);
    setActionLoading(true);
    try { 
      const earnings = await apiGetDriverEarnings(driver.id); 
      setDriverEarnings(earnings || { 
        total: `₹${driver.wallet || 0}`, 
        breakdown: { weekly: "₹8,500", monthly: "₹32,000", yearly: "₹1,85,000" } 
      }); 
    } catch (err) { 
      setDriverEarnings({ 
        total: `₹${driver.wallet || 0}`, 
        breakdown: { weekly: "₹8,500", monthly: "₹32,000", yearly: "₹1,85,000" } 
      }); 
    } finally { 
      setActionLoading(false); 
    }
  };

  const handleViewWallet = async (driver) => {
    setSelectedDriver(driver);
    setShowWalletModal(true);
    setActionLoading(true);
    try { 
      const wallet = await apiGetDriverWallet(driver.id); 
      setDriverWallet(wallet || { 
        balance: `₹${parseFloat(driver.wallet || 0).toLocaleString()}`, 
        transactions: [
          { date: "2024-02-01", amount: "+₹5,000", type: "credit" }, 
          { date: "2024-01-28", amount: "-₹2,500", type: "debit" }
        ] 
      }); 
    } catch (err) { 
      setDriverWallet({ 
        balance: `₹${parseFloat(driver.wallet || 0).toLocaleString()}`, 
        transactions: [
          { date: "2024-02-01", amount: "+₹5,000", type: "credit" }, 
          { date: "2024-01-28", amount: "-₹2,500", type: "debit" }
        ] 
      }); 
    } finally { 
      setActionLoading(false); 
    }
  };

  const handleViewLocation = async (driver) => {
    setSelectedDriver(driver);
    setShowLocationModal(true);
    setActionLoading(true);
    try { 
      const location = await apiGetDriverLiveLocation(driver.id); 
      setDriverLocation(location || { lat: driver.latitude || 28.6139, lng: driver.longitude || 77.2090 }); 
    } catch (err) { 
      setDriverLocation({ lat: driver.latitude || 28.6139, lng: driver.longitude || 77.2090 }); 
    } finally { 
      setActionLoading(false); 
    }
  };

  const handleViewHistory = async (driver) => {
    setSelectedDriver(driver);
    setShowHistoryModal(true);
    setActionLoading(true);
    try { 
      const history = await apiGetDriverDeliveryHistory(driver.id); 
      setDriverHistory(history || [
        { id: "DEL-001", date: "2024-02-15", amount: "₹250", status: "completed", restaurant: "Pizza Hut", customer: "John Doe" }
      ]); 
    } catch (err) { 
      setDriverHistory([
        { id: "DEL-001", date: "2024-02-15", amount: "₹250", status: "completed", restaurant: "Pizza Hut", customer: "John Doe" }
      ]); 
    } finally { 
      setActionLoading(false); 
    }
  };

  const handleAssignOrder = (driver) => { 
    setSelectedDriver(driver); 
    setAssignOrderData({ orderId: "", type: "manual" }); 
    setShowAssignOrderModal(true); 
  };
  
  const submitAssignOrder = async () => {
    if (!assignOrderData.orderId) { 
      showToast("Please enter Order ID", "error"); 
      return; 
    }
    setActionLoading(true);
    try { 
      await apiAssignOrder(selectedDriver.id, assignOrderData.orderId, assignOrderData.type === "manual"); 
      showToast(`Order assigned to ${selectedDriver.name || selectedDriver.displayName}`); 
      setShowAssignOrderModal(false); 
    } catch (err) { 
      showToast(`Assignment failed: ${err.message}`, "error"); 
    } finally { 
      setActionLoading(false); 
    }
  };

  const exportToCSV = () => {
    const headers = ['SN', 'ID', 'Status', 'Date', 'Ready', 'ON/OFF', 'Driver Name', 'Contact', 'Verified', 'Wallet'];
    const csvData = mappedDrivers.map(d => [
      d.sn, d.id, d.statusText, d.displayDate, d.is_ready === "1" ? "Yes" : "No", 
      d.open_status === "1" ? "ON" : "OFF", d.displayName, d.contact, 
      (d.is_verify_personalinfo === "1" && d.is_verify_docinfo === "1" && d.is_verify_vehicle === "1" && d.is_verify_bankinfo === "1" && d.is_verify_emergency === "1") ? "Yes" : "No", 
      d.displayWallet
    ]);
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `drivers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Exported successfully");
  };

  // Column widths - optimized to prevent horizontal scroll
  const columnWidths = {
    checkbox: "32px",
    sn: "40px",
    id: "50px",
    pic: "44px",
    status: "70px",
    date: "85px",
    ready: "55px",
    onoff: "55px",
    name: "120px",
    contact: "95px",
    verification: "75px",
    wallet: "75px",
    actions: "85px"
  };

  const styles = {
    container: { minHeight: "100vh", background: colors.background, fontFamily: "'DM Sans', sans-serif", padding: "16px 20px" },
    headerActions: { display: "flex", gap: "10px", marginBottom: "16px", flexWrap: "wrap" },
    addBtn: { background: colors.addBtnBg, color: "white", border: "none", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "12px", display: "flex", alignItems: "center", gap: "5px" },
    exportBtn: { background: colors.exportBtnBg, color: "white", border: "none", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "12px" },
    filtersBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" },
    searchWrapper: { position: "relative", flex: 1, maxWidth: "280px" },
    searchInput: { width: "100%", padding: "8px 10px 8px 32px", background: colors.inputBg, border: `1px solid ${colors.inputBorder}`, borderRadius: "8px", fontSize: "12px", color: colors.textPrimary, outline: "none" },
    searchIcon: { position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#3b82f6" },
    filterControls: { display: "flex", gap: "15px", alignItems: "center", flexWrap: "wrap" },
    filterGroup: { display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: colors.textMuted },
    filterButtons: { display: "flex", gap: "4px" },
    filterBtn: (isActive) => ({ 
      padding: "3px 8px", borderRadius: "5px", fontSize: "11px", 
      background: isActive ? colors.filterBtnActiveBg : colors.filterBtnInactiveBg, 
      color: isActive ? colors.filterBtnActiveColor : colors.filterBtnInactiveColor, 
      border: isActive ? `1px solid ${colors.filterBtnActiveBg}` : `1px solid ${colors.filterBtnInactiveBorder}`, 
      cursor: "pointer" 
    }),
    selectionBar: { display: "flex", alignItems: "center", gap: "10px", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: "8px", padding: "6px 12px", marginBottom: "10px", flexWrap: "wrap" },
    tableWrapper: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: "12px", overflow: "hidden", width: "100%" },
    table: { width: "100%", borderCollapse: "collapse", tableLayout: "fixed" },
    th: (width) => ({ 
      padding: "10px 6px", textAlign: "left", fontSize: "10px", fontWeight: 700, 
      color: "#3b82f6", borderBottom: `1px solid ${colors.border}`, 
      background: colors.tableHeaderBg, width: width || "auto", whiteSpace: "nowrap" 
    }),
    td: { 
      padding: "8px 6px", borderBottom: `1px solid ${colors.tableRowBorder}`, 
      fontSize: "11px", color: colors.textPrimary, whiteSpace: "nowrap", 
      overflow: "hidden", textOverflow: "ellipsis" 
    },
    pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: `1px solid ${colors.border}`, background: colors.tableHeaderBg, flexWrap: "wrap", gap: "10px" },
    viewMoreBtn: { 
      background: colors.surfaceLighter, border: `1px solid ${colors.border}`, 
      borderRadius: "6px", padding: "4px 10px", cursor: "pointer", 
      display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", 
      fontWeight: 500, color: colors.textPrimary, whiteSpace: "nowrap" 
    },
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button 
          key={i} 
          onClick={() => setPage(i)} 
          style={{ 
            width: "26px", height: "26px", borderRadius: "5px", 
            border: page === i ? `1px solid #3b82f6` : `1px solid ${colors.border}`, 
            background: page === i ? colors.buttonPrimaryBg : colors.surface, 
            color: page === i ? colors.buttonPrimaryColor : colors.textSecondary, 
            cursor: "pointer", fontSize: "10px" 
          }}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideUp { from{transform:translateY(16px);opacity:0} to{transform:translateY(0);opacity:1} } 
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        input::placeholder { color: ${isDark ? "#3a4a6b" : "#9ca3af"}; }
        button:hover { opacity: 0.9; transform: translateY(-1px); }
        button:active { transform: translateY(0); }
        @media (max-width: 1200px) { .driver-table { font-size: 10px; } }
      `}</style>

      <div style={styles.headerActions}>
        <button style={styles.addBtn} onClick={handleAddDriverNavigate}>
          <PlusIcon /> Add Driver
        </button>
        <button style={styles.exportBtn} onClick={exportToCSV}>
          📊 Export CSV
        </button>
      </div>

      <div style={styles.filtersBar}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}><SearchIcon /></span>
          <input 
            type="text" 
            style={styles.searchInput} 
            placeholder="Search drivers..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
          />
        </div>
        <div style={styles.filterControls}>
          <div style={styles.filterGroup}>
            <label>Status:</label>
            <div style={styles.filterButtons}>
              <button style={styles.filterBtn(statusFilter === "All")} onClick={() => { setStatusFilter("All"); setPage(1); }}>All</button>
              <button style={styles.filterBtn(statusFilter === "Active")} onClick={() => { setStatusFilter("Active"); setPage(1); }}>Active</button>
              <button style={styles.filterBtn(statusFilter === "Inactive")} onClick={() => { setStatusFilter("Inactive"); setPage(1); }}>Inactive</button>
            </div>
          </div>
          <div style={styles.filterGroup}>
            <label>Ready:</label>
            <div style={styles.filterButtons}>
              <button style={styles.filterBtn(readyFilter === "All")} onClick={() => { setReadyFilter("All"); setPage(1); }}>All</button>
              <button style={styles.filterBtn(readyFilter === "Yes")} onClick={() => { setReadyFilter("Yes"); setPage(1); }}>Yes</button>
              <button style={styles.filterBtn(readyFilter === "No")} onClick={() => { setReadyFilter("No"); setPage(1); }}>No</button>
            </div>
          </div>
          <div style={styles.filterGroup}>
            <label>ON/OFF:</label>
            <div style={styles.filterButtons}>
              <button style={styles.filterBtn(onOffFilter === "All")} onClick={() => { setOnOffFilter("All"); setPage(1); }}>All</button>
              <button style={styles.filterBtn(onOffFilter === "ON")} onClick={() => { setOnOffFilter("ON"); setPage(1); }}>ON</button>
              <button style={styles.filterBtn(onOffFilter === "OFF")} onClick={() => { setOnOffFilter("OFF"); setPage(1); }}>OFF</button>
            </div>
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div style={styles.selectionBar}>
          <span style={{ fontSize: "11px", color: "#93c5fd", fontWeight: 600 }}>
            ✓ {selected.size} driver{selected.size > 1 ? "s" : ""} selected
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={clearSelection} style={{ padding: "3px 8px", borderRadius: "5px", border: `1px solid ${colors.border}`, background: colors.surfaceLighter, color: colors.textSecondary, fontSize: "10px", cursor: "pointer" }}>
            Clear
          </button>
          <button onClick={() => setConfirmDelete(true)} style={{ padding: "3px 8px", borderRadius: "5px", border: "1px solid rgba(239,68,68,0.3)", background: colors.deleteBg, color: colors.deleteColor, fontSize: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
            <TrashIcon /> Delete
          </button>
        </div>
      )}

      <div style={styles.tableWrapper}>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th(columnWidths.checkbox)}><Checkbox checked={allPageSelected} onChange={toggleAll} theme={theme} /></th>
                <th style={styles.th(columnWidths.sn)}>SN</th>
                <th style={styles.th(columnWidths.id)}>ID</th>
                <th style={styles.th(columnWidths.pic)}>Pic</th>
                <th style={styles.th(columnWidths.status)}>Status</th>
                <th style={styles.th(columnWidths.date)}>Date</th>
                <th style={styles.th(columnWidths.ready)}>Ready</th>
                <th style={styles.th(columnWidths.onoff)}>ON/OFF</th>
                <th style={styles.th(columnWidths.name)}>Driver Name</th>
                <th style={styles.th(columnWidths.contact)}>Contact</th>
                <th style={styles.th(columnWidths.verification)}>Verification</th>
                <th style={styles.th(columnWidths.wallet)}>Wallet</th>
                <th style={styles.th(columnWidths.actions)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: perPage }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 13 }).map((_, j) => (
                      <td key={j} style={styles.td}>
                        <div style={{ height: "10px", background: colors.border, borderRadius: "3px", width: j === 0 ? "12px" : "70%" }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : mappedDrivers.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ padding: "50px", textAlign: "center", color: colors.textMuted, fontSize: "12px" }}>
                    {debouncedSearch ? "No drivers match your search" : "No drivers found"}
                  </td>
                </tr>
              ) : (
                mappedDrivers.map((driver) => (
                  <tr key={driver.id} style={{ background: selected.has(driver.id?.toString()) ? colors.selectedBg : "transparent" }}>
                    <td style={styles.td}><Checkbox checked={selected.has(driver.id?.toString())} onChange={() => toggleOne(driver.id?.toString())} theme={theme} /></td>
                    <td style={styles.td}>{driver.sn}</td>
                    <td style={styles.td}>{driver.id}</td>
                    <td style={styles.td}>
                      <DriverImage 
                        picName={driver.pic} 
                        name={driver.displayName} 
                        theme={theme} 
                      />
                    </td>
                    <td style={styles.td}><StatusBadge status={driver.statusText} theme={theme} /></td>
                    <td style={styles.td}>{driver.displayDate}</td>
                    <td style={styles.td}><ReadyBadge is_ready={driver.is_ready} theme={theme} /></td>
                    <td style={styles.td}><OnOffBadge open_status={driver.open_status} theme={theme} /></td>
                    <td style={styles.td}><strong style={{ fontSize: "11px" }}>{driver.displayName}</strong></td>
                    <td style={styles.td}>{driver.contact}</td>
                    <td style={styles.td}><VerificationBadge driver={driver} theme={theme} /></td>
                    <td style={styles.td}>{driver.displayWallet}</td>
                    <td style={styles.td}>
                      <button style={styles.viewMoreBtn} onClick={() => handleViewMoreClick(driver)}>
                        <ViewMoreIcon /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={styles.pagination}>
            <span style={{ fontSize: "10px", color: colors.textMuted }}>
              {loading ? "Loading..." : `Showing ${startIndex + 1}–${Math.min(startIndex + perPage, totalFiltered)} of ${totalFiltered} drivers`}
            </span>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))} 
                disabled={page === 1 || loading} 
                style={{ width: "26px", height: "26px", borderRadius: "5px", border: `1px solid ${colors.border}`, background: colors.surface, color: page === 1 ? colors.textMuted : colors.textSecondary, cursor: "pointer", fontSize: "10px" }}
              >
                <ChevronLeftIcon />
              </button>
              {renderPageNumbers()}
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                disabled={page === totalPages || loading} 
                style={{ width: "26px", height: "26px", borderRadius: "5px", border: `1px solid ${colors.border}`, background: colors.surface, color: page === totalPages ? colors.textMuted : colors.textSecondary, cursor: "pointer", fontSize: "10px" }}
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div onClick={() => setConfirmDelete(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: colors.modalBg, border: `1px solid ${colors.modalBorder}`, borderRadius: "12px", padding: "20px", width: "320px" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: "14px", color: colors.textPrimary }}>Confirm Delete</h3>
            <p style={{ fontSize: "12px", color: colors.textSecondary, marginBottom: "16px" }}>
              Delete {selected.size} driver{selected.size > 1 ? "s" : ""}? This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => setConfirmDelete(false)} style={{ flex: 1, padding: "8px", borderRadius: "6px", border: `1px solid ${colors.border}`, background: colors.surfaceLighter, color: colors.textSecondary, cursor: "pointer", fontSize: "11px" }}>Cancel</button>
              <button onClick={handleDeleteSelected} disabled={actionLoading} style={{ flex: 1, padding: "8px", borderRadius: "6px", border: "none", background: colors.deleteBg, color: colors.deleteColor, cursor: "pointer", fontSize: "11px" }}>
                {actionLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {showDocumentsModal && selectedDriver && (
        <div onClick={() => setShowDocumentsModal(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: colors.modalBg, border: `1px solid ${colors.modalBorder}`, borderRadius: "12px", padding: "20px", width: "400px", maxHeight: "80vh", overflow: "auto" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "14px", color: colors.textPrimary }}>Document Verification</h3>
            <p style={{ fontSize: "12px", marginBottom: "16px" }}>Driver: <strong>{selectedDriver.displayName}</strong></p>
            {[
              { label: "Personal Info", key: "personalinfo", value: selectedDriver.is_verify_personalinfo },
              { label: "Document Info", key: "docinfo", value: selectedDriver.is_verify_docinfo },
              { label: "Vehicle Info", key: "vehicle", value: selectedDriver.is_verify_vehicle },
              { label: "Bank Info", key: "bankinfo", value: selectedDriver.is_verify_bankinfo },
              { label: "Emergency Info", key: "emergency", value: selectedDriver.is_verify_emergency }
            ].map(f => (
              <div key={f.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: "11px" }}>{f.label}: {f.value === "1" ? "✅ Verified" : "⏳ Pending"}</span>
                {f.value !== "1" && (
                  <button onClick={() => verifyDocument(f.key)} style={{ padding: "2px 8px", borderRadius: "4px", background: colors.buttonPrimaryBg, color: colors.buttonPrimaryColor, border: "none", cursor: "pointer", fontSize: "9px" }}>
                    Verify
                  </button>
                )}
              </div>
            ))}
            <button onClick={() => setShowDocumentsModal(false)} style={{ marginTop: "16px", width: "100%", padding: "8px", borderRadius: "6px", border: `1px solid ${colors.border}`, background: colors.surfaceLighter, color: colors.textSecondary, cursor: "pointer", fontSize: "11px" }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Earnings Modal */}
      {showEarningsModal && driverEarnings && (
        <div onClick={() => setShowEarningsModal(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: colors.modalBg, border: `1px solid ${colors.modalBorder}`, borderRadius: "12px", padding: "20px", width: "320px" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: "14px", color: colors.textPrimary }}>Driver Earnings</h3>
            <p style={{ fontSize: "11px", color: colors.textSecondary, marginBottom: "16px" }}>Driver: <strong>{selectedDriver?.displayName}</strong></p>
            <div style={{ textAlign: "center", marginBottom: "16px" }}>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#22c55e" }}>{driverEarnings.total}</div>
              <div style={{ fontSize: "10px", color: colors.textMuted }}>Total Earnings</div>
            </div>
            {driverEarnings.breakdown && (
              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "11px" }}>
                  <span>Weekly:</span><span>{driverEarnings.breakdown.weekly}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "11px" }}>
                  <span>Monthly:</span><span>{driverEarnings.breakdown.monthly}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px" }}>
                  <span>Yearly:</span><span>{driverEarnings.breakdown.yearly}</span>
                </div>
              </div>
            )}
            <button onClick={() => setShowEarningsModal(false)} style={{ marginTop: "16px", width: "100%", padding: "8px", borderRadius: "6px", border: `1px solid ${colors.border}`, background: colors.surfaceLighter, color: colors.textSecondary, cursor: "pointer", fontSize: "11px" }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Assign Order Modal */}
      {showAssignOrderModal && selectedDriver && (
        <div onClick={() => setShowAssignOrderModal(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: colors.modalBg, border: `1px solid ${colors.modalBorder}`, borderRadius: "12px", padding: "20px", width: "350px" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: "14px", color: colors.textPrimary }}>Assign Order</h3>
            <p style={{ fontSize: "11px", color: colors.textSecondary, marginBottom: "16px" }}>Driver: <strong>{selectedDriver.displayName}</strong></p>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "10px", color: colors.textMuted, display: "block", marginBottom: "4px" }}>Order ID</label>
              <input 
                value={assignOrderData.orderId} 
                onChange={e => setAssignOrderData(p => ({ ...p, orderId: e.target.value }))} 
                style={{ width: "100%", padding: "6px 10px", background: colors.inputBg, border: `1px solid ${colors.inputBorder}`, borderRadius: "6px", color: colors.textPrimary, fontSize: "11px" }} 
                placeholder="Enter Order ID" 
              />
            </div>
            <div style={{ marginBottom: "16px", display: "flex", gap: "12px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px" }}>
                <input type="radio" value="manual" checked={assignOrderData.type === "manual"} onChange={() => setAssignOrderData(p => ({ ...p, type: "manual" }))} /> Manual
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px" }}>
                <input type="radio" value="auto" checked={assignOrderData.type === "auto"} onChange={() => setAssignOrderData(p => ({ ...p, type: "auto" }))} /> Auto
              </label>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => setShowAssignOrderModal(false)} style={{ flex: 1, padding: "6px", borderRadius: "6px", border: `1px solid ${colors.border}`, background: colors.surfaceLighter, color: colors.textSecondary, cursor: "pointer", fontSize: "11px" }}>
                Cancel
              </button>
              <button onClick={submitAssignOrder} disabled={actionLoading} style={{ flex: 1, padding: "6px", borderRadius: "6px", border: "none", background: colors.buttonPrimaryBg, color: colors.buttonPrimaryColor, cursor: "pointer", fontSize: "11px" }}>
                {actionLoading ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} theme={theme} />}
    </div>
  );
};

export default DriverTable;